import { get, set, del, createStore } from "idb-keyval";
import { api } from "./api";

const store = createStore("hf-offline", "kv");
const listeners = new Set();
let syncing = false;
let again = false;

export const isNetErr = (e) => !e?.response;
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
  (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)));
export const newId = uuid;

const currentUserId = () => { try { return JSON.parse(localStorage.getItem("hf_user") || "null")?.id; } catch { return null; } };

const notify = async () => { const s = await syncState(); listeners.forEach((fn) => fn(s)); };
export const subscribe = (fn) => { listeners.add(fn); syncState().then(fn); return () => listeners.delete(fn); };

export const getQueue = async () => (await get("queue", store)) || [];
const setQueue = (q) => set("queue", q, store);
export const getLocalBins = async () => (await get("lbins", store)) || {};
const setLocalBins = (m) => set("lbins", m, store);
export const getBundle = () => get("bundle", store);

export const syncState = async () => {
  const q = await getQueue();
  return { online: navigator.onLine, syncing, pending: q.filter((o) => o.status === "pending").length,
    conflicts: q.filter((o) => o.status === "conflict"), total: q.length };
};

export const refreshBundle = async () => {
  try { const { data } = await api.get("/offline/bundle"); await set("bundle", data, store); return data; }
  catch { return getBundle(); }
};

export const cacheBin = async (bin) => set(`bin:${bin.id}`, bin, store);
export const getCachedBin = (id) => get(`bin:${id}`, store);

export const getLocalBin = async (id) => (await getLocalBins())[id];
export const saveLocalBin = async (bin) => { const m = await getLocalBins(); m[bin.id] = bin; await setLocalBins(m); };

export const findLabel = async (code) => {
  const b = await getBundle();
  const c = String(code).trim();
  return b?.labels.find((l) => l.token === c || l.number === c.toUpperCase());
};

export const offlineRelease = async (sectorId, varietyId, date) => {
  const b = await getBundle();
  const r = (b?.releases || []).find((x) => x.sector_id === sectorId && x.variety_id === varietyId && x.valid_from <= date && x.valid_to >= date);
  return { release: r || null, status: r ? r.status : "none" };
};

export const enqueue = async (op) => {
  const q = await getQueue();
  q.push({ op_id: uuid(), status: "pending", created_at: new Date().toISOString(), user_id: currentUserId(), ...op });
  await setQueue(q);
  notify();
  if (navigator.onLine) { if (syncing) again = true; else sync(); }
};

const exec = async (op) => {
  if (op.type === "activate") return api.post("/bins/activate", op.payload);
  if (op.type === "photo") {
    const fd = new FormData();
    fd.append("file", op.payload.blob, "photo.jpg");
    fd.append("source", op.payload.source);
    fd.append("taken_at", op.payload.taken_at);
    return api.post(`/bins/${op.bin_id}/photo`, fd);
  }
  return api.post(`/bins/${op.bin_id}/${op.type}`, op.payload || {});
};

export const sync = async () => {
  if (syncing) return;
  syncing = true; notify();
  try {
    let q = await getQueue();
    const failedBins = new Set(q.filter((o) => o.status === "conflict").map((o) => o.bin_id));
    const me = currentUserId();
    for (const op of q.filter((o) => o.status === "pending" && o.user_id === me)) {
      if (failedBins.has(op.bin_id)) continue;
      try {
        await exec(op);
        q = (await getQueue()).filter((o) => o.op_id !== op.op_id);
      } catch (e) {
        if (isNetErr(e) || e.response.status >= 500 || e.response.status === 401) break;
        failedBins.add(op.bin_id);
        q = (await getQueue()).map((o) => (o.op_id === op.op_id ? { ...o, status: "conflict", error: e.response?.data?.detail || "error" } : o));
      }
      await setQueue(q);
    }
    const left = new Set(q.map((o) => o.bin_id));
    const bins = await getLocalBins();
    Object.keys(bins).forEach((id) => { if (!left.has(id)) delete bins[id]; });
    await setLocalBins(bins);
    if (!q.length) refreshBundle();
  } finally {
    syncing = false; notify();
    if (again) { again = false; setTimeout(sync, 300); }
  }
};

export const discardBin = async (binId) => {
  await setQueue((await getQueue()).filter((o) => o.bin_id !== binId));
  const bins = await getLocalBins(); delete bins[binId]; await setLocalBins(bins);
  notify();
};

export const clearOffline = async () => { for (const k of ["queue", "lbins", "bundle"]) await del(k, store); notify(); };

// Mimics api.post for field actions on a local (offline) bin, queueing each action.
export const offlineClient = (binId, policy) => ({
  post: async (url, body) => {
    const action = url.split("/").pop();
    const bin = await getLocalBin(binId);
    const now = new Date().toISOString();
    let queued = null;
    const fail = (detail) => Promise.reject({ response: { status: 400, data: { detail } } });
    if (action === "weight") {
      const k = body.method === "unable" ? null : body.kg;
      const target = policy?.target_net_kg || 120;
      const diff = k == null ? undefined : Math.round((k - target) * 100) / 100;
      bin.field_weight = { method: body.method, kg: k, at: now, target_kg: target, diff_kg: diff,
        diff_pct: diff === undefined ? undefined : Math.round((diff / target) * 1000) / 10,
        classification: k == null ? "unable" : policy?.tolerance_under_kg == null || policy?.tolerance_over_kg == null ? "policy_not_configured"
          : diff < -policy.tolerance_under_kg ? "under" : diff > policy.tolerance_over_kg ? "over" : "within" };
      queued = { type: "weight", bin_id: binId, payload: { ...body, client_at: now } };
    } else if (action === "photo") {
      const blob = body.get("file");
      bin.photo = { source: body.get("source"), at: now, blob };
      queued = { type: "photo", bin_id: binId, payload: { blob, source: body.get("source"), taken_at: now } };
    } else if (action === "ready") {
      if (!bin.photo || !bin.field_weight) return fail("photo_and_weight_required");
      bin.status = "ready";
      queued = { type: "ready", bin_id: binId, payload: { client_at: now } };
    } else if (action === "sent") {
      bin.status = "sent";
      queued = { type: "sent", bin_id: binId, payload: { client_at: now } };
    }
    bin.history = [...(bin.history || []), { id: uuid(), action: action === "weight" ? "field_weight" : action, by_name: bin.activated_by_name, at: now, offline: true }];
    await saveLocalBin(bin);
    if (queued) await enqueue(queued);
    return { data: bin };
  },
});
